<script setup>
import {ref, computed, watch, onMounted, getCurrentInstance, nextTick} from 'vue';
import helpers from "@/lib/helpers";

import LabelModal from "../label/label-modal.vue";
import ItemInventoryEdit from "./item-inventory-edit.vue";
import ItemInventoryLocationHistory from "./item-inventory-location-history.vue";
import ItemInventoryFilter from "./item-inventory-filter.vue";
import AuditItemDetails from "../audit/audit-item-details.vue";
import ListingFactsList from "../listing/listing-facts-list.vue";
import ListingFactsEdit from "../listing/listing-facts-edit.vue";
import NotificationWatch from "../notification/notification-watch.vue";

const dialog = getCurrentInstance().proxy.$dialog

const emit = defineEmits('selected', 'loaded')

const props = defineProps({
  url: String,
  wide: Boolean,
  noPortal: Boolean,
  // the full width of whatever holds it, rather than the centered container width
  fluid: Boolean,
  readonly: Boolean,
  photoReadonly: Boolean,
  photoNoUpload: Boolean,
  apiFilter: String,
  auctionToolbar: Boolean,
  photoClickEvent: Boolean,
  noOrder: Boolean,
  noSelecting: Boolean,
  // the host shows its own overlay for this load - held up until the `loaded`
  // event - so firing the global bar as well is two spinners for one fetch
  quiet: Boolean,
  hover: Boolean,
  showOnly: [String,Number],
  preSelect: Array,
  highlight: {
    type: Array,
    default() {
      return [];
    }
  },
});

const formatters = {
  user(v) {
    return v ? v.userid : ''
  },
  location(v) {
    return v ? (v.name + (v.warehouse ? ' @ ' + v.warehouse.name : '')) : ''
  },
  date(v) {
    return v ? new Date(v).toLocaleString() : ''
  },
  gli(v) {
    return v ? v.gli : ''
  }
}
const fieldsDef = {
  summary: [
    {
      key: 'warehouse',
      sortable: true,
      sortByFormatted: true,
      formatter: (a, b, r) => r.location && r.location.warehouse && r.location.warehouse.name,
      tdClass: 'text-nowrap',
    },
    {
      key: 'location',
      sortable: true,
      label: 'Location',
      sortByFormatted: true,
      formatter: (v) => v && v.name,
      tdClass: 'text-nowrap'
    },
    {key: 'item_condition', sortable: true, label: 'Grade'},
    {key: 'serialno', sortable: true, label: 'Serial'},
    // what the unit's own listing is asking. Sits between the serial and the
    // listing it belongs to, so the three read as one thought
    {
      key: 'listed_price',
      sortable: true,
      label: 'Price',
      sortByFormatted: true,
      formatter: (v, k, i) => (i.auction && Number(i.auction.price) > 0) ? Number(i.auction.price) : null,
      tdClass: 'text-nowrap text-right',
      thClass: 'text-right'
    },
    {key: 'auction', sortable: true, label: 'eBay', tdClass: 'text-nowrap', formatter: formatters.gli},
    {key: 'order', sortable: true, label: 'Order', tdClass: 'text-nowrap', formatter: formatters.gli}
  ],
  listings: [
    {key: 'auction', sortable: true, label: 'eBay', tdClass: 'text-nowrap', formatter: formatters.gli},
    {
      key: 'auction_review',
      sortable: true,
      label: 'Review',
      sortByFormatted: true,
      formatter: (v, k, i) => i.auction ? (i.auction.review_required ? 'YES' : 'NO') : ''
    },
    {
      key: 'auction_fullstatus',
      sortable: true,
      label: 'Status',
      sortByFormatted: true,
      formatter: (v, k, i) => i.auction ? i.auction.full_status : '',
      tcClass: 'text-nowrap'
    },
    {
      key: 'auction_title',
      sortable: true,
      label: 'Title',
      sortByFormatted: true,
      formatter: (v, k, i) => i.auction ? i.auction.eb_Title : ''
    }
  ],
  more: [
    // moved here when the summary tab traded it for the listing price - the
    // PO is provenance, which is what this tab is for
    {key: 'po', sortable: true, label: 'PO', formatter: formatters.gli, tdClass: 'text-nowrap'},
    {
      key: 'added',
      sortable: true,
      label: 'Recvd',
      sortByFormatted: true,
      formatter: (v) => new Date(v).toLocaleString()
    },
    {key: 'createdby_user', sortable: true, label: 'Recvd', sortByFormatted: true, formatter: formatters.user},
    {key: 'testing_desc', sortable: true, label: 'Testing', sortByFormatted: true,},
    {key: 'user1', sortable: true, label: 'User 1'},
    {key: 'user2', sortable: true, label: 'User 2'}
  ],
  wip: [
    {key: 'cleanby_user', sortable: true, label: 'Clean By', sortByFormatted: true, formatter: formatters.user},
    {key: 'clean_date', sortable: true, label: 'Clean On', sortByFormatted: true, formatter: formatters.date},
    {
      key: 'clean_stop',
      sortable: true,
      label: 'Clean Time',
      sortByFormatted: true, formatter: (v, k, i) => {
        if (!v || !i.clean_date)
          return null
        return moment(v).diff(moment(i.clean_date), 'minutes') + ' mins'
      }
    },
    {
      key: 'clean', sortable: true, label: 'Clean Type', sortByFormatted: true, formatter: (v) => {
        const c = ['Minimal', 'Normal', 'Extra']
        return c[v]
      }
    },
    {key: 'photo', sortable: true, label: 'Photo', sortByFormatted: true, formatter: (v) => v ? 'YES' : 'NO'}

  ],
  notes: [
    {key: 'notes', sortable: true, label: 'Notes', tdClass: 'w-100 text-wrap'}
  ],
  // what the technician answered at the bench, one line per question
  facts: [
    {key: 'listing_facts', sortable: false, label: 'Receiving Answers', tdClass: 'w-100 text-wrap'}
  ],
  photos: [
    {key: 'photos', sortable: false, label: 'Photos', tdClass: 'w-100'}
  ],
  warehouse: [
    {
      key: 'initbin_location',
      sortable: true,
      label: 'Recvd To',
      sortByFormatted: true, formatter: formatters.location,
      tdClass: 'text-nowrap'
    },
    {key: 'putawayby_user', sortable: true, label: 'Putaway', sortByFormatted: true, formatter: formatters.user},
    {
      key: 'putaway_location',
      sortable: true,
      label: 'Putaway',
      sortByFormatted: true, formatter: formatters.location,
      tdClass: 'text-nowrap'
    },
    {key: 'putaway_date', sortable: true, label: 'Putaway', sortByFormatted: true, formatter: formatters.date},
    {key: 'lastmoveby_user', sortable: true, label: 'Move', sortByFormatted: true, formatter: formatters.user},
    {
      key: 'lastbin_location',
      sortable: true,
      label: 'Move',
      sortByFormatted: true, formatter: formatters.location,
      tdClass: 'text-nowrap'
    },
    {key: 'lastmove_date', sortable: true, label: 'Move', sortByFormatted: true, formatter: formatters.date},
  ],
  sale: [
    {key: 'order', sortable: true, label: 'Order', tdClass: 'text-nowrap', formatter: formatters.gli},
    {
      key: 'pickedfrom_location',
      sortable: true,
      label: 'Picked',
      sortByFormatted: true, formatter: formatters.location,
      tdClass: 'text-nowrap'
    },
    {
      key: 'pickedby_user',
      sortable: true,
      label: 'Picked',
      sortByFormatted: true,
      formatter: (v, k, i) => i.pickedby ? formatters.user(v) : ''
    },
    {key: 'pickdate', sortable: true, label: 'Picked', sortByFormatted: true, formatter: formatters.date},
    {
      key: 'packedby_user',
      sortable: true,
      label: 'Packed',
      sortByFormatted: true,
      formatter: (v, k, i) => i.pickedby ? formatters.user(v) : ''
    },
  ],
  loss: [
    {
      key: 'scraploss_date',
      sortable: true,
      label: 'Date',
      sortByFormatted: true,
      formatter: formatters.date,
      tdClass: 'text-nowrap'
    },
    {key: 'loss_type', sortable: true, label: 'Type'},
    {
      key: 'scraplossby_user',
      sortable: true,
      label: 'Loss',
      sortByFormatted: true,
      formatter: formatters.user,
      tdClass: 'text-nowrap'
    },
    {
      key: 'loss_location',
      sortable: true,
      label: 'From',
      sortByFormatted: true,
      formatter: formatters.location,
      tdClass: 'text-nowrap'
    },
    {key: 'loss_reason', sortable: true, label: 'Reason', tdClass: 'w-100'},
  ],
  disposition: [
    {key: 'order', sortable: true, label: 'Order', tdClass: 'text-nowrap', formatter: formatters.gli},
    {
      key: 'disposition',
      sortable: true,
      label: 'Disposition',
      sortByFormatted: true, formatter: (v) => v ? v.description : '',
      tdClass: 'w-100'
    }
  ]
}


const item = ref(null);
const mode = ref('summary');
const urlFilter = ref(props.apiFilter || 'onhand');
const filter = ref(null);
const selected = ref([]);
const filteredItems = ref([]);

const tableRef = ref()

const printLabel = (url) => {
  dialog.show(LabelModal, {
    setting: 'printers.inventory',
    type: 'LabelInventory',
    url
  });
};

const remove = (remove) => {
  if (item.value && item.value.inventory) {
    item.value.inventory = item.value.inventory.filter((i) => i.id !== remove.id);
  }
};

const rowClick = (r, index) => {
  if (!props.noSelecting) {
   tableRef.value.selectRow(index);
  }
};

const setPreSelect = (preSelect) => {
  if (preSelect) {
    table.value.forEach((row, index) => {
      if (preSelect.find(r => r == row.id)) {
       tableRef.value.selectRow(index);
      }
    });
  }
};

const toSku = (data) => {
  let photos = (data.selected.length ? data.selected : [data.photo]);

  if (!photos.includes(data.photo)) {
    photos.push(data.photo);
  }

  helpers.confirm('Copy ' + photos.length + ' photo(s) to master SKU?').then(() => {
    helpers.asyncAction(item.value._links.photo_url.href, item.value._links.photo_url.method, {
      data: {
        task: 'copy',
        photo_add: photos.map(p => ({id: p.id}))
      }
    }).then((response) =>
        helpers.toast('Successfully copied ' + response.files.length + ' photo(s) to SKU photos.')
    );
  });
};

const onFiltered = (fi) => {
  filteredItems.value = fi;
};

const filterSelected = (invert) => {
  filter.value = filter.value || {
    boolean: [],
    booleanop: 'N',
    texts: [],
    byid: []
  };
  const selectedid = selected.value.map(i => i.id);
  if (invert) {
    let items = (filteredItems.value.length ? filteredItems.value : table.value).map(i => i.id);
    filter.value.byid = items.filter(x => !selectedid.includes(x));
  } else {
    filter.value.byid = selectedid;
  }

 tableRef.value.clearSelected();
};

const onRowSelected = (items) => {
  if (props.noSelecting) {
   tableRef.value.clearSelected();
  } else {
    selected.value = items;
  }
};

const formatCol = (key, value) => {
  let result = value;
  Object.keys(fields).forEach((k, i) => {
    fields[k].forEach(f => {
      if (f.key == key && f.formatter) {
        result = f.formatter(value);
      }
    });
  });

  return result || '';
};

const tableFilter = (row, filter) => {
  if (!filter) return true;

  if (filter.byid.length) {
    if (!filter.byid.includes(row.id)) return false;
  }

  const boolean = {
    listed: (row) => !!row.auction,
    not_listed: (row) => !row.auction,
    orderable: (row) => row.orderable,
    not_orderable: (row) => !row.orderable,
    photos: (row) => row.photos ? !!row.photos.length : false,
    not_photos: (row) => row.photos ? !row.photos.length : true,
    scrapped: (row) => !!row.scrap_date,
    adjusted: (row) => !!row.loss_date,
    sold: (row) => !!row.order,
    returned: (row) => !!row.disposition,
    putaway: (row) => !!row.putaway_date,
    not_putaway: (row) => !row.putaway_date,
  };

  let result = true;
  if (filter.booleanop !== 'N') {
    result = (filter.booleanop === 'AND');
    filter.boolean.forEach((f) => {
      if (filter.booleanop === 'OR') {
        result |= boolean[f](row);
      } else {
        result &= boolean[f](row);
      }
    });
  }
  if (!result) return false;

  if (Array.isArray(filter.grades)) {
    result &= (filter.grades.includes(row.item_condition ? row.item_condition.toUpperCase() : 'U'));
  }

  if (!result) return false;

  filter.texts.forEach((text) => {
    if (text.query) {
      result &= !!(formatCol(text.key, row[text.key]).match(text.query));
    }
  });

  return result;
};

const refresh = async () => {
  item.value = null;
  item.value = await helpers.asyncAction(props.url + '?with[]=inventory&inventory_filter=' + urlFilter.value, 'GET', {noload: props.quiet});
  nextTick(() =>tableRef.value.clearSelected());
  selected.value = [];
  emit('loaded');
};

const fields = computed(() => {
  return [
    {key: 'selected', label: ' '},
    {key: 'gli', sortable: true, label: 'SKU', tdClass: 'text-nowrap', stickyColumn: false},
    ...fieldsDef[mode.value],
    {key: 'task', sortable: false, label: '', tdClass: 'p-0 text-right align-middle'}
  ].filter(f => !props.noOrder || f.key !== 'order');
});

const table = computed(() => {
  if (!item.value || !item.value.inventory) return [];

  return item.value.inventory.map((r) => {
    return {
      loss_type: r.scrap_date ? 'SCRAP' : (r.loss_date ? 'ADJ' : ''),
      scraploss_date: r.scrap_date || r.loss_date,
      scraplossby_user: r.scrap_date ? r.scrap_by_user : r.lossby_user,
      scraploss_location_name: r.loss_location ? r.loss_location.name : '',
      _cellVariants: {gli: (r.onhand ? (r.auctionable ? '' : 'info') : 'danger')},
      _rowVariant: props.highlight.includes(r.id) ? 'warning' : '',
      ...r
    };
  }).filter((r) => !props.showOnly || props.showOnly == r.id);
});

onMounted(() => {
  refresh();
  setPreSelect();
});

watch(() => urlFilter.value, refresh);
watch(() => selected.value, (v) => {
  emit('selected', v);
});
watch(() => props.noSelecting, (v) => {
  if (v) {
   tableRef.value.clearSelected();
  }
});

defineExpose({setPreSelect})
</script>
<template xmlns="http://www.w3.org/1999/html">
  <div>
    <b-container :fluid="fluid">
      <!-- wide: the full page's inv-buttons target; else the enclosing modal's top strip -->
      <component :is="noPortal ? 'b-col' : (wide ? 'portal' : 'modal-portal')" :to="wide ? 'inv-buttons' : 'top'" class="my-auto">
        <div :class="{'mt-3': wide}">
          <b-row v-if="auctionToolbar" class="mb-2">
            <b-col cols="6">
              <mode-selector breakpoint="sm" v-model="mode" :modes="[
                  {text: 'Summary', value: 'summary'},
                    {text: 'More', value: 'more', html: (item.user1 || item.user2) ? '<div class=\'bg-warning text-white h-100 p-0 m-0 w-100 rounded\'>More</div>' : null},
                    {text: 'Photos', value: 'photos'},
                    can('items.inventory.notes.read') ? {text: 'Notes', value: 'notes'} : null,
                    {text: 'Answers', value: 'facts'}
                ]"/>
            </b-col>
            <b-col cols="6" class="text-right" v-if="!noSelecting">
              <b-button-group>
                <b-btn variant="outline-primary" size="sm" @click="tableRef.selectAllRows()">All</b-btn>
                <b-btn variant="outline-primary" size="sm" @click="tableRef.clearSelected()">Clear</b-btn>
              </b-button-group>
            </b-col>
          </b-row>

          <b-button-group v-else :class="{'mb-3': wide, 'mb-2': noPortal, 'float-right': true}">
            <b-btn size="sm" disabled variant="outline-secondary">
              <template v-if="selected && selected.length">{{ selected.length }} /</template>
              {{ filteredItems.length ? filteredItems.length : table.length }}
            </b-btn>
            <b-dropdown size="sm" ref="viewdrop" style="min-width: 85px" text="View">
              <b-dropdown-item-button @click="mode = 'summary'" :active="mode == 'summary'">Summary
              </b-dropdown-item-button>
              <b-dropdown-item-button @click="mode = 'more'" :active="mode == 'more'">More Details
              </b-dropdown-item-button>
              <b-dropdown-item-button @click="mode = 'wip'" :active="mode == 'wip'">WIP</b-dropdown-item-button>
              <b-dropdown-item-button @click="mode = 'warehouse'" :active="mode == 'warehouse'">Warehouse
              </b-dropdown-item-button>
              <b-dropdown-item-button @click="mode = 'sale'" :active="mode == 'sale'" v-if="urlFilter != 'auctionable'">
                Sale
              </b-dropdown-item-button>
              <b-dropdown-item-button block class="my-auto" @click="mode = 'photos'" :active="mode == 'photos'">Photos
              </b-dropdown-item-button>
              <b-dropdown-item-button block class="my-auto" @click="mode = 'notes'" :active="mode == 'notes'"
                                      v-if="can('items.inventory.notes.read')">Notes
              </b-dropdown-item-button>
              <b-dropdown-item-button block class="my-auto" @click="mode = 'facts'" :active="mode == 'facts'">Answers
              </b-dropdown-item-button>
              <b-dropdown-item-button block class="my-auto" @click="mode = 'listings'" :active="mode == 'listings'"
                                      v-if="urlFilter != 'auctionable'">
                Listings
              </b-dropdown-item-button>
              <b-dropdown-item-button block class="my-auto" @click="mode = 'disposition'"
                                      v-if="urlFilter != 'auctionable'"
                                      :active="mode == 'disposition'">
                Disposition
              </b-dropdown-item-button>
              <b-dropdown-item-button block class="my-auto" @click="mode = 'loss'" :active="mode == 'loss'"
                                      v-if="urlFilter == 'historic'">
                Adjust / Scrap
              </b-dropdown-item-button>
            </b-dropdown>

            <b-dropdown size="sm" style="min-width: 85px" text="Filter" :variant="filter ? 'warning' : ''">
              <template>
                <b-dropdown-item-button @click="urlFilter = 'onhand'" :active="urlFilter == 'onhand'">On Hand
                </b-dropdown-item-button>
                <b-dropdown-item-button @click="urlFilter = 'historic'" :active="urlFilter == 'historic'">Historic
                </b-dropdown-item-button>
                <b-dropdown-divider/>
              </template>
              <template v-if="!!filter">
                <b-dropdown-item-button @click="filter = null">Clear Filter</b-dropdown-item-button>
                <b-dropdown-divider/>
              </template>
              <b-dropdown-item-button @click="tableRef.selectAllRows()">Select All</b-dropdown-item-button>
              <b-dropdown-item-button @click="tableRef.clearSelected()">Unselect All</b-dropdown-item-button>
              <b-dropdown-divider/>
              <b-dropdown-item-button @click="filterSelected(false)">Selected</b-dropdown-item-button>
              <b-dropdown-item-button @click="filterSelected(true)">Not Selected</b-dropdown-item-button>
              <b-dropdown-divider/>
              <b-dropdown-item-button
                  @click="$dialog.showAndWait(ItemInventoryFilter, {value: filter}).then((f) => (f && (filter = f)))">
                Advanced
                Filter
              </b-dropdown-item-button>
            </b-dropdown>

            <b-dropdown size="sm" style="min-width: 85px" text="Tasks">
              <b-dropdown-item-button v-if="!!selected.length" @click="tableRef.clearSelected()">Unselect All
              </b-dropdown-item-button>
              <b-dropdown-item-button @click="refresh">Refresh</b-dropdown-item-button>
              <template v-if="!readonly">
                <b-dropdown-divider/>
                <b-dropdown-item-button @click="printLabel( selected.map(s => s._links.label_url))"
                                        :disabled="!selected.length">Print
                  Labels
                </b-dropdown-item-button>
                <b-dropdown-item-button v-if="urlFilter === 'onhand'"
                                        @click="$dialog.showAndWait(ItemInventoryEdit, {item: item, value: [...selected]}).then((v) => v && refresh())"
                                        :disabled="!selected.length">
                  Edit
                </b-dropdown-item-button>
              </template>
            </b-dropdown>
          </b-button-group>
        </div>
      </component>

      <b-table
          head-variant="light"
          :style="wide ? '' : 'min-height: 300px'"
          ref="tableRef"
          responsive="md"
          :selectable="true"
          sortByFormatted
          :fields="fields"
          :items="table"
          :filter="filter"
          :filter-function="tableFilter"
          @row-selected="onRowSelected"
          @filtered="onFiltered"
          selected-variant="secondary"
          :sticky-header="wide ? null : '390px'"
          :hover="hover"
      >
        <template #cell(selected)="{ rowSelected, item }">
          <slot name="selected-cell" :item="item">
            <div style="width: 15px">
              <font-awesome-icon v-if="rowSelected" icon="fa-solid fa-check"/>
            </div>
          </slot>
        </template>
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <strong class="my-5">Loading...</strong>
          </div>
        </template>
        <template #cell(photos)="data">
          <photo-gallery
              with-meta
              :no-upload="photoNoUpload"
              :inputId="data.item.gli"
              :photos="data.value"
              :readonly="photoReadonly"
              :uploadUrl="data.item._links.photo_url.href"
              @click="p => $emit('photo-click', p)"
              :click-event="photoClickEvent"
              :width="120">
            <template #tasks="data">
              <b-dropdown-item-button @click="toSku(data)"><font-awesome-icon :icon="['far', 'floppy-disk']" class="mr-2" fixed-width/>Save to SKU</b-dropdown-item-button>
            </template>
          </photo-gallery>
        </template>
        <template #cell(listed_price)="data">
          <span v-if="data.value">{{ data.value | money }}</span>
        </template>
        <template #cell(po)="data">
          <b-link v-if="!!data.item.po" :href="'/po/' + data.item.po.id">{{ data.value }}</b-link>
        </template>
        <template #cell(order)="data">
          <b-link v-if="!!data.item.order" :href="'/order/' + data.item.order.id">{{ data.value }}</b-link>
        </template>
        <template #cell(auction)="data">
          <b-link v-if="!!data.item.auction" :href="'/auction/' + data.item.auction.id">{{ data.value }}</b-link>
        </template>
        <template #cell(notes)="data">
          <span style="white-space: pre-wrap;" v-text="data.value"/>
        </template>
        <template #cell(listing_facts)="data">
          <listing-facts-list :facts="data.value" compact/>
        </template>
        <template #cell(task)="data" v-if="!readonly">
          <b-dropdown variant="link" no-caret size="sm" :disabled="!!selected.length">
            <template #button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis-v"/>
            </template>
            <b-dropdown-item-button @click="printLabel(data.item._links.label_url)">Print Label</b-dropdown-item-button>
            <b-dropdown-item-button v-if="urlFilter == 'onhand'"
                                    @click="$dialog.showAndWait(ItemInventoryEdit, {item: item, value: [data.item]}).then((v) => v && refresh())">
              Edit
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="urlFilter == 'onhand' && can('items.inventory.modify')"
                                    @click="$dialog.showAndWait(ListingFactsEdit, {itemId: item.id, unitId: data.item.id, label: String(data.item.gli || '')}).then((v) => v && refresh())">
              Edit Answers
            </b-dropdown-item-button>
            <b-dropdown-divider/>
            <b-dropdown-item-button @click="$dialog.show(ItemInventoryLocationHistory, {inventory: data.item})">Location
              History
            </b-dropdown-item-button>
            <b-dropdown-item-button
                @click="$dialog.show(AuditItemDetails, {noclose: true, auditable: {countable_type: 'inventory', countable_id: data.item.id}})">
              Cycle Count
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="can('notifications.rules.read')"
                @click="$dialog.show(NotificationWatch, {type: 'Inventory', id: data.item.id, gli: data.item.gli})">
              Notifications
            </b-dropdown-item-button>
            <b-dropdown-item
                :to="{name: 'report-run', params: {id: 28}, query: {batch: true, record: data.item.id, type: 'stockinventory'}}"
                target="_blank">
              Audit
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-container>
  </div>
</template>


<style scoped>
.hasuser {
  background-color: red
}
</style>